// ==========================================
// FILE: src/components/features/PetWalker/PetWalkerRegistration.js
// Component for users to register as pet walkers
// ==========================================
import React, { useState } from 'react';
import { X, MapPin, DollarSign, Clock } from 'lucide-react';
import { useAPI } from '../../../hooks/useAPI';
import LocationPicker from '../Search/LocationPicker';

const PetWalkerRegistration = ({ user, onClose, onSuccess }) => {
  const { apiCall } = useAPI();
  const [formData, setFormData] = useState({
    bio: '',
    experience_years: '',
    hourly_rate: '',
    service_area: '',
    latitude: null,
    longitude: null,
    availability: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    },
    services: {
      dog_walking: false,
      pet_sitting: false,
      overnight_care: false,
      dog_training: false
    },
    certifications: '',
    phone: user?.phone || '',
    emergency_contact: ''
  });
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleLocationSelected = (location) => {
    setFormData({
      ...formData,
      service_area: location.name,
      latitude: location.latitude,
      longitude: location.longitude
    });
    setShowLocationPicker(false);
  };

  const handleAvailabilityToggle = (day) => {
    setFormData({
      ...formData,
      availability: {
        ...formData.availability,
        [day]: !formData.availability[day]
      }
    });
  };

  const handleServiceToggle = (service) => {
    setFormData({
      ...formData,
      services: {
        ...formData.services,
        [service]: !formData.services[service]
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.bio.trim()) {
      setError('Please provide a bio about yourself');
      return;
    }
    if (!formData.service_area || !formData.latitude) {
      setError('Please select your service area on the map');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return;
    }
    if (!Object.values(formData.services).some(v => v)) {
      setError('Please select at least one service you provide');
      return;
    }
    if (!Object.values(formData.availability).some(v => v)) {
      setError('Please select at least one day of availability');
      return;
    }

    setSubmitting(true);

    try {
      await apiCall('/petwalker/register', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      alert('Successfully registered as a pet walker!');
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-xl w-full max-w-3xl my-8 max-h-screen overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b p-6 z-10 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">🐕 Become a Pet Walker</h2>
              <p className="text-sm text-gray-600 mt-1">Fill out your profile to start offering pet care services</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Bio */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                About You <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell pet owners about your experience, personality, and why you love caring for animals..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                rows={4}
                required
              />
            </div>

            {/* Experience & Rate */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Clock size={16} className="inline mr-1" />
                  Years of Experience
                </label>
                <input
                  type="number"
                  value={formData.experience_years}
                  onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                  placeholder="e.g., 3"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <DollarSign size={16} className="inline mr-1" />
                  Hourly Rate (USD)
                </label>
                <input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  placeholder="e.g., 25"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>
            </div>

            {/* Service Area */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin size={16} className="inline mr-1" />
                Service Area <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.service_area}
                  readOnly
                  placeholder="Click 'Select on Map' to choose your service area"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
                <button
                  type="button"
                  onClick={() => setShowLocationPicker(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold"
                >
                  Select on Map
                </button>
              </div>
            </div>

            {/* Services Offered */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Services You Offer <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'dog_walking', label: '🐕 Dog Walking' },
                  { key: 'pet_sitting', label: '🏠 Pet Sitting' },
                  { key: 'overnight_care', label: '🌙 Overnight Care' },
                  { key: 'dog_training', label: '🎓 Dog Training' }
                ].map(service => (
                  <button
                    key={service.key}
                    type="button"
                    onClick={() => handleServiceToggle(service.key)}
                    className={`px-4 py-3 rounded-lg border-2 font-medium transition ${
                      formData.services[service.key]
                        ? 'bg-purple-500 text-white border-purple-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    {service.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Availability <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.keys(formData.availability).map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleAvailabilityToggle(day)}
                    className={`px-3 py-2 rounded-lg border-2 font-medium transition capitalize ${
                      formData.availability[day]
                        ? 'bg-green-500 text-white border-green-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-green-300'
                    }`}
                  >
                    {day.substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Emergency Contact
                </label>
                <input
                  type="tel"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  placeholder="Emergency contact number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>
            </div>

            {/* Certifications */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Certifications & Training
              </label>
              <textarea
                value={formData.certifications}
                onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                placeholder="List any pet care certifications, training, or special qualifications..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                rows={2}
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-semibold disabled:opacity-50"
              >
                {submitting ? 'Registering...' : 'Register as Pet Walker'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPicker
          onLocationSelected={handleLocationSelected}
          onClose={() => setShowLocationPicker(false)}
        />
      )}
    </>
  );
};

export default PetWalkerRegistration;